import {
  AdInterface,
  AdInterfaceKeys,
  FormReducerAction,
  FormReducerState
} from '../contexts/types/ad';
import {
  Error,
  GetCurrentContentProps,
  GetPhotosRules,
  GetPriceRules,
  GetService,
  GetServiceWithCategory,
  Image,
  ServiceParams,
  UseAd
} from './types/useAdTypes';
import { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { AdActions } from '../actions/ad';
import { AxiosError } from 'axios';
import ListingsService from '../../../services/Listings.service';
import { MakeOptional } from '../../utils/make-optional';
import _ from 'lodash';
import { toBase64 } from '../../../utils/format';
import toastify from '../../lib/toastify';
import { updateCurrentAd } from '../../services/Ad.service';
import { useAdContext } from '../contexts/ad';

export function useAd(): UseAd {
  const history = useHistory();
  const location = useLocation();
  const {
    currentAd,
    dispatchForm,
    formState,
    setCumulativeChanges,
    openEditView,
    cumulativeChanges,
    setCurrentTabDepth
  } = useAdContext();
  const { invalidatePricesCache } = AdActions();
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  const queryClient = useQueryClient();

  const baseUrl = openEditView
    ? location.pathname.replace('/' + openEditView, '')
    : location.pathname;

  const getCurrentContent = useCallback(currentContentCallback, [dispatchForm]);
  const getCachedContent = useCallback(cachedContentCallback, [
    error,
    formState,
    getCurrentContent,
    initialLoading
  ]);

  const updateAdMutation = useMutation({
    mutationFn: ({ content }: { content: MakeOptional<AdInterface>; redirect: boolean }) =>
      updateCurrentAd(currentAd?.id ? currentAd.id : 0, content),
    onSuccess: (_, { content, redirect }) => {
      if (redirect) {
        invalidateDynamicContent(content);
        onSavedContent();
      }
    },
    onError: (err, { content }) => {
      const error = err as AxiosError;
      if (error?.isAxiosError && error?.response?.status === 400) {
        onFieldMissingError(content);
      } else {
        toastify.error(
          'seu anúncio não foi salvo',
          'Não conseguimos atualizar seu anúncio neste momento.'
        );
      }
    }
  });


  const setCurrentContent = useCallback(
    (content: MakeOptional<AdInterface>, redirect = true) => {
      updateAdMutation.mutate({
        content,
        redirect
      });
    },
    [updateAdMutation]
  );

  function onSavedContent() {
    setCumulativeChanges(undefined);
    setCurrentTabDepth(0);
    setError(undefined);
    queryClient.invalidateQueries(['current-ad', currentAd?.id]);

    toastify.success('alterações salvas', 'seu anúncio foi alterado com sucesso.');
    history.push(baseUrl);
  }

  function onFieldMissingError(content: MakeOptional<AdInterface>) {
    setError({
      fieldMissing: true
    });
    pushToCumulativeChanges(content);
  }

  async function currentContentCallback(
    path: string,
    dispatchType: FormReducerAction['type'],
    serviceParams?: ServiceParams
  ) {
    try {
      setInitialLoading(true);
      const selectedService = getService(path) as (
        serviceParams?: ServiceParams
      ) => Promise<
        GetService[] | GetServiceWithCategory[] | GetPriceRules | GetPhotosRules | undefined
      >;
      const response = await selectedService(serviceParams);
      dispatchForm({
        type: dispatchType,
        payload: response || []
      });
      return response;
    } catch (err) {
      toastify.error('falha na requisição', 'não foi possível buscar os dados solicitados.');
      console.log(err);
      setError({
        failedToFetch: true
      });
      return undefined;
    } finally {
      setInitialLoading(false);
    }
  }

  async function setNewImages(images: Image[]) {
    setLoading(true);

    const { fotos, id } = currentAd || {};
    const orderOfFirstImage = _.get(fotos, ['0', 'order'], 0);

    const newImages = images.filter(elem => elem.action === 'upload');

    try {
      for (let i = 0, dataLength = newImages.length; i < dataLength; i += 1) {
        const currentImage = newImages[i];
        const order = +currentImage.index + +orderOfFirstImage;
        await ListingsService.saveImage(id, {
          id: null,
          ordem: order,
          mimetype: 'image/*',
          conteudo: await toBase64(currentImage.file)
        });
      }

      queryClient.invalidateQueries(['current-ad', currentAd?.id]);
      toastify.success('alterações salvas', 'seu anúncio foi alterado com sucesso.');
      history.goBack();
    } catch (error) {
      toastify.error(
        'seu anúncio não foi salvo',
        'Não conseguimos atualizar seu anúncio neste momento.'
      );
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  function getService(serviceName: string) {
    const currentService = serviceName as keyof typeof ListingsService;
    return ListingsService[currentService];
  }

  function pushToEditForm(path: string) {
    const baseUrl = location.pathname.replace(/\/$/, '');
    history.push(`${baseUrl}/${path}`);
  }

  function cachedContentCallback<FormStateType extends keyof FormReducerState>(
    cacheToFetch: keyof FormReducerState,
    { path, dispatchType, serviceParams }: GetCurrentContentProps
  ): FormReducerState[FormStateType] | undefined {
    if (initialLoading && formState[cacheToFetch])
      return formState[cacheToFetch] as FormReducerState[FormStateType];

    if (initialLoading) return;

    if (formState[cacheToFetch]) {
      return formState[cacheToFetch] as FormReducerState[FormStateType];
    }

    if (error) return;

    getCurrentContent(path, dispatchType, serviceParams);
  }

  function invalidateDynamicContent(content: MakeOptional<AdInterface>) {
    if (content.valorVenda || content?.veiculo?.versao?.id) {
      invalidatePricesCache();
    }
  }

  function pushToCumulativeChanges(content: MakeOptional<AdInterface>) {
    setCumulativeChanges(() => {
      const result = Object.entries(content).reduce((acc, current) => {
        const [key, value] = current;
        const typedKey = key as AdInterfaceKeys;
        if (typeof value !== 'object') {
          return {
            ...acc,
            [key]: value
          };
        } else {
          const cumulative = acc ? acc[typedKey] : {};
          return {
            ...acc,
            [typedKey]: {
              ...(cumulative && typeof cumulative === 'object' ? cumulative : {}),
              ...value
            }
          };
        }
      }, cumulativeChanges);

      return result;
    });
  }

  return {
    setCurrentContent,
    getCurrentContent,
    setNewImages,
    pushToEditForm,
    loading: loading || updateAdMutation.isLoading,
    initialLoading,
    error,
    getCachedContent,
    pushToCumulativeChanges
  };
}
